import React, { FC, useCallback, useContext, useEffect, useRef, useState } from 'react';
import { mixpanelTrack } from '@providers/Mixpanel';
import useNoodleApi from '@hooks/useNoodleApi';
import * as tsClient from '@tsClient';
import ProgressBarLoader from '@/components/ProgressBarLoader';
import ProgressIndicator from '@/components/ProgressIndicator';
import Button from '@/components/DesignLibrary/Button';
import WarningCircle from '@/components/Icons/WarningCircle';
import XCircle from '@components/Icons/XCircle';
import Bluebird from 'bluebird';
import formatAmountWithCurrency from '@format/priceWithCurrency';
import { Currency } from '@/typings/api-models';
import { useOpenDocumentRequestUserFilePane } from '@/panes/documentRequestUserFileHooks';

import {
  Document,
  DocumentRequestFile,
  DocumentRequestFileStatus,
  DocumentRequestStatus,
  DocumentRequestUserFile,
  PaystubAnalysis } from '@/tsClient/servers/noodleDocuments/document-api-models';
import DocumentFileCard from '@components/Document/DocumentFileCard';
import FileArrowDown from '@components/Icons/FileArrowDown';
import Plus from '@components/Icons/Plus';
import Workflow from '@components/Icons/Workflow';
import Spacer from '@components/Spacer';
import { DocumentAccessContext, DocumentAccessGate } from '@providers/DocumentAccess';
import { useIsMobile, useToast, useUser } from '@hooks';
import CustomLink from '@components/CustomLink';
import { IDENTIFIERS, getUrl, getUrlBase } from '@helpers/urlsHelper';
import preloadDocument from '@helpers/preloadDocument';
import { logError } from '@providers/ErrorTracking';
import teamsContext from '@providers/Teams/TeamsContext';
import Buttons from '@components/Buttons/Buttons';
import Modal from '@components/Modal';
import UserImage from '@components/UserImage';
import { getUserWorkflowById, getUserWorkflowCollaborators } from '@tsClient/workflows';
import AddDocumentModal from '@modals/AddDocumentModal';
import Head from 'next/head';
import Header from '@components/DesignLibrary/Header';
import userWorkflowToContext from '@helpers/userWorkflowToContext';
import PaperPlaneRight from '@components/Icons/PaperPlaneRight';
import Check from '@components/Icons/Check';
import Reopen from '@components/Icons/Reopen';
import Tabs from '@components/DesignLibrary/Tabs';
import { SIZES } from '@styles/media';
import BannerStatus from '@components/DesignLibrary/BannerStatus';
import MainPanelSubLayout from '@layouts/MainPanelSubLayout';
import DashboardContext from '@/layouts/DashboardLayout/DashboardContext';
import UserWorkflowDiscussion from '@lib/Creators/Dashboard/Workflows/UserWorkflowWidgets/UserWorkflowDiscussion';
import { useRouter } from 'next/router';
import * as format from '@format';
import { useEventsContext } from '@/providers/Events';
import AddIncomeSourceModal from '@/modals/AddIncomeSourceModal';
import ToggleLeft from '@/components/Icons/ToggleLeft';
import ToggleRight from '@/components/Icons/ToggleRight';
import { TBody, TD, TH, THead, Table, TableRow } from '@/components/DesignLibrary/Table';
import { Checkbox, Input } from '@/components/DesignLibrary/Atoms';
import { useFeatureFlag } from '@/providers/FeatureFlags';
import { ArrowsOutLineVertical, ArrowsInLineVertical, IconWeight } from '@phosphor-icons/react';
import pluralize from 'pluralize';
import CaretUp from '@components/Icons/CaretUp';
import CaretDown from '@components/Icons/CaretDown';
import Label from '@/components/DesignLibrary/Atoms/shared/Label';
import { useQueryState } from 'nuqs';
import { DOC_ID_QS_KEY } from '@/lib/DocumentRequestUserFileDetail/DocumentRequestUserFileDetail';

import { WorkflowCollaborator } from '@/hooks/useUserWorkflowContext';
import FilePdf from '@/components/Icons/FilePdf';
import { getConsolidatedData } from '@/tsClient/documentRequests';
import NoodleAI from '@/components/Icons/NoodleAI';
import Tooltip from '@/components/Tooltip';
import useSocketContext from '@/providers/Socket/useSocketContext';
import { SocketMessageType } from '@/providers/Socket';
import removeNullish from '@/helpers/removeNullish';
import AddPayOrganizerItem from '@modals/AddPayOrganizerItem';
import { PAY_ORGANIZER_KEY_TO_LABEL, TABLE_COLUMNS } from '@lib/DocumentRequestUserDetailV3/constants';
import s from './DocumentRequestUserDetail.module.scss';
import PayOrganizerFilePreview from './PayOrganizerFilePreview';
import MeansTestSection from './MeansTestSection';
import { FlatConsolidatedData } from './Types';
import SchedulesICalculator, { SchedulesIResponse } from './SchedulesICalculator';
import calculateNetPay from './calculateNetPay';

type ThisDocumentRequestUserFile = Pick<
  DocumentRequestUserFile,
  'id' | 'status' | 'isRequired' | 'hasPermission' | 'order' | 'fileName' | 'fileDescription' | 'isForYourEyesOnly' | 'rejectedAt' | 'rejectionMessage'
> & {
  documentRequestFile: Pick<DocumentRequestFile, 'fileName' | 'fileDescription' | 'order' | 'isForYourEyesOnly'> | null;
  documents: Array<{
    document: Pick<Document, 'id' | 'fileName' | 'createdAt' | 'uploadedBy' | 'mimeType' | 'uploadedAt'> | null;
    rank: number;
    fileDocumentId: string;
  }>;
};
type Props = {
  documentRequestUserId: string;
  creatorSlug: string;
};

type Tabs = 'Overview' | 'Discussion';

const DocumentRequestUserDetail: FC<Props> = ({ documentRequestUserId, creatorSlug }) => {
  const [isTableViewOpen, setIsTableViewOpen] = useState(false);
  const [isCalculatorsViewOpen, setIsCalculatorsViewOpen] = useState(false);
  const [documentRequestUserFiles, setDocumentRequestUserFiles] = useState<ThisDocumentRequestUserFile[]>([]);
  const [documentRequest, setDocumentRequest] = useState<Awaited<ReturnType<typeof tsClient.documentRequests.getDocumentRequestUser>> | null>(null);
  const userWorkflowId = documentRequest?.userWorkflowId;
  const { accessToken } = useContext(DocumentAccessContext);
  const { creatorId } = useContext(teamsContext);
  const [compiledBlobUrl, setCompiledBlobUrl] = useState<string | null>(null);
  const [isUpdatingDocumentRequest, setIsUpdatingDocumentRequest] = useState(false);
  const [collaborators, setCollaborators] = useState<WorkflowCollaborator[]>();
  const [isConfirmSubmitModalOpen, setIsConfirmSubmitModalOpen] = useState(false);
  const [currentTab, setCurrentTab] = useState<'pending' | 'uploaded'>('pending');
  const [isAddDocumentModalOpen, setAddDocumentModalOpen] = useState(false);
  const [containerOffset, setContainerOffset] = useState(180);
  const [valueModalOpen, setValueModalOpen] = useState(false);
  const [modalOpen, setModalOpen] = useState<string | null>(null);
  const [userFileIdForAddLine, setUserFileIdForAddLine] = useState<string | null>(null);
  const [payDateSortDirection, setPayDateSortDirection] = useState<'asc' | 'desc'>("asc");
  const canShowTable = useFeatureFlag('can-show-pay-organizer-table');
  const canShowSchedulesICalculator = useFeatureFlag('show-schedules-i-calculator');
  const [_, setDocumentId] = useQueryState(DOC_ID_QS_KEY);
  const [filePreview, setFilePreview] = useState<{
    documentRequestUserFileId: string;
    documentId: string;
  } | null>(null);
  const [user] = useUser();
  const userId = user?.id;
  const isMobile = useIsMobile();
  const isTabsLayout = useIsMobile(SIZES.lg);
  const containerRef = useRef<HTMLDivElement | null>(null);
  const { isInDashboard } = useContext(DashboardContext);
  const router = useRouter();
  const [consolidatedData, setConsolidatedData] = useState<{
    rows: (FlatConsolidatedData & {
      months: Array<FlatConsolidatedData & {
        rows: FlatConsolidatedData[];
      }>;
    })[];
    total: FlatConsolidatedData;
      } | null>(null);
  const [collapsedRows, setCollapsedRows] = useState<string[]>([]);
  const [selectedDocuments, setSelectedDocuments] = useState<string[]>([]);
  const [selectedForMeanTest, setSelectedForMeanTest] = useState<string[]>([]);
  const [isRemovingDocument, setIsRemovingDocument] = useState(false);

  useEffect(() => {
    mixpanelTrack('DocumentRequest - viewed', { creatorSlug, documentRequestUserId });
  }, [creatorSlug, documentRequestUserId]);

  const { data: documentRequestUser, getData: getDocumentRequestUserFn } = useNoodleApi(tsClient.documentRequests.getDocumentRequestUser);
  const { getData: updateInternalDataFn } = useNoodleApi(tsClient.documentRequests.updateInternalDataForDocument);
  const { getData: removeDocument } = useNoodleApi(tsClient.documentRequests.removeDocumentFromDocumentRequestUserFile);
  const openDocumentRequestUserFilePane = useOpenDocumentRequestUserFilePane();

  const {
    data: documentRequestUserFilesLoaded,
    getData: getDocumentRequestUserFilesFn,
    fetchingState: { isFetching: isFetchingDocumentRequestUserFiles },
  } = useNoodleApi(tsClient.documentRequests.getDocumentRequestUserFiles);

  const { getData: getDocumentRequestUserFile } = useNoodleApi(tsClient.documentRequests.getDocumentRequestUserFile);

  const { data: creator, getData: getCreatorFn } = useNoodleApi(tsClient.getCreator);

  const { getData: completeDocumentRequestFn } = useNoodleApi(tsClient.documentRequests.completeDocumentRequest, {
    toastOnError: true,
    toastSuccessMessage: () => [useToast.ToastTypeVariants.SUCCESS, 'Document request completed successfully'],
  });
  const { getData: reOpenDocumentRequestFn } = useNoodleApi(tsClient.documentRequests.reOpenDocumentRequest, {
    toastOnError: true,
    toastSuccessMessage: () => [useToast.ToastTypeVariants.SUCCESS, 'Document request reopened successfully'],
  });
  const { getData: updateDocumentRequestUser } = useNoodleApi(tsClient.documentRequests.updateDocumentRequestUser, {
    toastOnError: true,
    toastSuccessMessage: () => [useToast.ToastTypeVariants.SUCCESS, 'Document request updated successfully'],
  });
  const { getData: updateDocumentRequestUserModal } = useNoodleApi(tsClient.documentRequests.updateDocumentRequestUserForModalState, {
    toastOnError: true,
  });
  const {
    data: workflowData,
    fetchingState: fetchingWorkflow,
    getData: getWorkflow,
  } = useNoodleApi(getUserWorkflowById, {
    toastOnError: true,
  });

  const { data: loadedCollaborators, getData: getCollaboratorsFn } = useNoodleApi(getUserWorkflowCollaborators);

  const { data: fetchedData, fetchingState: fetchingConsolidatedDataState, getData:getConsolidatedDataFn} = useNoodleApi(getConsolidatedData);

  const {
    data: meansTestData,
    getData: getMeansTestDataFn,
    fetchingState: {isFetching: fetchingMeansTestData},
  } = useNoodleApi(tsClient.ai.getMeansTestData);

  const fetchConsolidatedData = useCallback(async (): Promise<void> => {
    const res = await getConsolidatedDataFn({
      documentRequestUserId,
    });
    if (res.data) {
      const getMonthArray = (file: Awaited<ReturnType<typeof getConsolidatedData>>['files'][number]): Array<FlatConsolidatedData & {
        rows: FlatConsolidatedData[];
      }> => {
        const months = file.documents
          .filter((d) => d.data)
          .reduce((acc: Record<string, Awaited<ReturnType<typeof getConsolidatedData>>['files'][number]['documents']>, val) => {
            const payDate = (val.data?.tableData as PaystubAnalysis)?.payDate;
            let month = 'Unknown Pay Date';
            if (payDate) {
              month = new Date(`${payDate}T00:00:00`).toLocaleDateString('default', { month: 'long', year: 'numeric' });
            }
            return {
              ...acc,
              [month]: acc[month] ? [...acc[month], val] : [val],
            };
          }, {});
        const getSum = ({
          data, key,
        }: {
          data: {
            document: Pick<Document, 'id' | 'fileName'> | null;
            data: Record<string, unknown>;
            fileDocumentId: string;
          }[], key: keyof PaystubAnalysis
        }): number => data.reduce((acc, val) => {
          const tableData = val.data?.tableData as PaystubAnalysis || null;
          if (!tableData || typeof tableData[key] !== 'number' || !val.data.useInCalculation) {
            return acc;
          }
          return acc + (tableData[key] as number);
        }, 0);

        return Object.entries(months).map(([month, data]) => ({
          "Amount per Pay Period": getSum({data, key: 'amountPerPayPeriod'}),
          "Business expenses": getSum({ data, key: 'businessExpenses' }),
          "Domestic support obligations": getSum({data, key: 'domesticSupportObligations'}),
          "Employer": month,
          "Insurance": getSum({data, key: 'insurance'}),
          "Mandatory contributions for retirement plans": getSum({data, key: 'mandatoryContributionsForRetirementPlans'}),
          "Net Pay": getSum({data, key: 'netPay'}),
          "Other deductions": getSum({data, key: 'otherDeductions'}),
          "Overtime per Pay Period": getSum({data, key: 'overtimePerPayPeriod'}),
          "Pay Date": (data[0].data.tableData as PaystubAnalysis)?.payDate,
          "Required repayments of retirement fund loans": getSum({data, key: 'requiredRepaymentsOfRetirementFundLoans'}),
          "Tax, Medicare, and Social Security deductions": getSum({data, key: 'taxMedicareAndSocialSecurityDeductions'}),
          "Total Gross Income": getSum({data, key: 'grossPay'}),
          "Union dues": getSum({data, key: 'unionDues'}),
          "Voluntary contributions for retirement plans": getSum({data, key: 'voluntaryContributionsForRetirementPlans'}),
          id: `${file.id}-${month}`,
          incomeSource: file.metadata?.incomeSource?? null,
          rows: [...data.map((document) => ({
            "Amount per Pay Period": (document.data.tableData as PaystubAnalysis)?.amountPerPayPeriod,
            "Business expenses": (document.data.tableData as PaystubAnalysis)?.businessExpenses,
            "Domestic support obligations": (document.data.tableData as PaystubAnalysis)?.domesticSupportObligations,
            "Employer": document.document?.fileName || (document.data.detailedExtraction as {employer:{ name:string } })?.employer?.name || (document.data.tableData as PaystubAnalysis)?.payDate || null,
            "Insurance": (document.data.tableData as PaystubAnalysis)?.insurance,
            "Mandatory contributions for retirement plans": (document.data.tableData as PaystubAnalysis)?.mandatoryContributionsForRetirementPlans,
            "Net Pay": (document.data.tableData as PaystubAnalysis)?.netPay,
            "Other deductions": (document.data.tableData as PaystubAnalysis)?.otherDeductions,
            "Overtime per Pay Period": (document.data.tableData as PaystubAnalysis)?.overtimePerPayPeriod,
            "Pay Date": (document.data.tableData as PaystubAnalysis)?.payDate,
            "Required repayments of retirement fund loans": (document.data.tableData as PaystubAnalysis)?.requiredRepaymentsOfRetirementFundLoans,
            "Tax, Medicare, and Social Security deductions": (document.data.tableData as PaystubAnalysis)?.taxMedicareAndSocialSecurityDeductions,
            "Total Gross Income": (document.data.tableData as PaystubAnalysis)?.grossPay,
            "Union dues": (document.data.tableData as PaystubAnalysis)?.unionDues,
            "Voluntary contributions for retirement plans": (document.data.tableData as PaystubAnalysis)?.voluntaryContributionsForRetirementPlans,
            id: document.fileDocumentId,
          })),
          ],
        }));
      };

      setConsolidatedData({
        rows: res.data.files.filter(file => file.data).map((file) => ({
          "Amount per Pay Period": (file.data as PaystubAnalysis).amountPerPayPeriod,
          "Business expenses": (file.data as PaystubAnalysis).businessExpenses,
          "Domestic support obligations": (file.data as PaystubAnalysis).domesticSupportObligations,
          "Employer": `${file.fileName} ${file.metadata?.incomeSource && file.metadata?.incomeSource!=='Employment'? '' : `(${file.documents.length} ${pluralize('paystub', file.documents.length)})`}`,
          "Insurance": (file.data as PaystubAnalysis).insurance,
          "Mandatory contributions for retirement plans": (file.data as PaystubAnalysis).mandatoryContributionsForRetirementPlans,
          "Net Pay": (file.data as PaystubAnalysis).netPay,
          "Other deductions": (file.data as PaystubAnalysis).otherDeductions,
          "Overtime per Pay Period": (file.data as PaystubAnalysis).overtimePerPayPeriod,
          "Pay Date": null,
          "Required repayments of retirement fund loans": (file.data as PaystubAnalysis).requiredRepaymentsOfRetirementFundLoans,
          "Tax, Medicare, and Social Security deductions": (file.data as PaystubAnalysis).taxMedicareAndSocialSecurityDeductions,
          "Total Gross Income": (file.data as PaystubAnalysis).grossPay,
          "Union dues": (file.data as PaystubAnalysis).unionDues,
          "Voluntary contributions for retirement plans": (file.data as PaystubAnalysis).voluntaryContributionsForRetirementPlans,
          id: file.id,
          incomeSource: file.metadata?.incomeSource?? null,
          months: getMonthArray(file),
        })),
        total: {
          "Amount per Pay Period": (((res.data.total as PaystubAnalysis).amountPerPayPeriod) ?? 0),
          "Business expenses": (((res.data.total as PaystubAnalysis).businessExpenses) ?? 0),
          "Domestic support obligations": (res.data.total as PaystubAnalysis).domesticSupportObligations,
          "Employer": null,
          "Include in Monthly Totals":'Monthly Totals for Schedule I',
          "Insurance": (res.data.total as PaystubAnalysis).insurance,
          "Mandatory contributions for retirement plans": (res.data.total as PaystubAnalysis).mandatoryContributionsForRetirementPlans,
          "Net Pay": (res.data.total as PaystubAnalysis).netPay,
          "Other deductions": (res.data.total as PaystubAnalysis).otherDeductions,
          "Overtime per Pay Period": (res.data.total as PaystubAnalysis).overtimePerPayPeriod,
          "Pay Date": null,
          "Required repayments of retirement fund loans": (res.data.total as PaystubAnalysis).requiredRepaymentsOfRetirementFundLoans,
          "Tax, Medicare, and Social Security deductions": (res.data.total as PaystubAnalysis).taxMedicareAndSocialSecurityDeductions,
          "Total Gross Income": (res.data.total as PaystubAnalysis).grossPay,
          "Union dues": (res.data.total as PaystubAnalysis).unionDues,
          "Voluntary contributions for retirement plans": (res.data.total as PaystubAnalysis).voluntaryContributionsForRetirementPlans,
          id: 'total',
        },
      });
      const useInCalculationIds = res.data.files.flatMap(item => {
        const calculationIds: string[] = [];
        const documentIds = item.documents
          .filter(doc => doc.data?.useInCalculation === true)
          .map(doc => doc.fileDocumentId);

        if (documentIds.length > 0) {
          calculationIds.push(...documentIds);
        }

        if (Array.isArray(item.metadata?.monthlyDollarValue)) {
          item.metadata.monthlyDollarValue.forEach(mdv => {
            if (mdv.useInCalculation) calculationIds.push(`${item.id}-${mdv.month}`);
          });
        }

        return calculationIds;
      });
      setSelectedDocuments(useInCalculationIds);

      const useInMeansTestIds = res.data.files.flatMap(item => {
        const meansTestIds: string[] = [];
        const documentIds = item.documents
          .filter(doc => doc.data?.useInMeansTest === true)
          .map(doc => doc.fileDocumentId);

        if (documentIds.length > 0) {
          meansTestIds.push(...documentIds);
        }

        if (Array.isArray(item.metadata?.monthlyDollarValue)) {
          item.metadata.monthlyDollarValue.forEach(mdv => {
            if (mdv.useInMeansTest) meansTestIds.push(`${item.id}-${mdv.month}`);
          });
        }

        return meansTestIds;
      });
      setSelectedForMeanTest(useInMeansTestIds);
    }
  }, [documentRequestUserId, getConsolidatedDataFn]);

  const removeFile = async ({ fileDocumentId, documentRequestUserFileId }: {
    fileDocumentId: string;
    documentRequestUserFileId: string;
  }): Promise<void> => {
    setIsRemovingDocument(true);
    await removeDocument({
      documentId: fileDocumentId,
      fileId: documentRequestUserFileId,
      id: documentRequestUserId,
    });
    await fetchConsolidatedData();
    setIsRemovingDocument(false);
  };

  const {
    addListener: addSocketListener,
    removeListener: removeSocketListener,
    isInitialized: isSocketInitialized,
    joinChannels,
  } = useSocketContext();

  useEffect(() => {
    if (isSocketInitialized && documentRequestUserId) {
      joinChannels([`ai-means-test-data/${documentRequestUserId}`]);
    }
  }, [joinChannels, documentRequestUserId, isSocketInitialized]);

  useEffect(() => {
    const listenerId = addSocketListener({
      fn: () => {
        if (userWorkflowId && documentRequestUserId && isTableViewOpen) {
          getMeansTestDataFn({ documentRequestUserId });
        }
      },
      messageType: SocketMessageType.MEANS_TEST_AI_REQUEST_STATUS_UPDATED,
    });

    return () => {
      removeSocketListener(listenerId);
    };
  }, [addSocketListener, removeSocketListener, getMeansTestDataFn, documentRequest?.id, userWorkflowId, documentRequestUserId, isTableViewOpen]);

  useEffect(() => {
    if (documentRequestUserId && isTableViewOpen) {
      fetchConsolidatedData();
    }
  }, [documentRequestUserId, fetchConsolidatedData, isTableViewOpen]);

  useEffect(() => {
    if (userWorkflowId && documentRequestUserId && isTableViewOpen) {
      getMeansTestDataFn({ documentRequestUserId });
    }
  }, [documentRequest?.id, userWorkflowId, documentRequestUserId, getMeansTestDataFn, isTableViewOpen]);

  const fetchCollaborators = useCallback(() => {
    if (workflowData?.id) {
      getCollaboratorsFn({ userWorkflowId: workflowData?.id });
    }
  },[workflowData, getCollaboratorsFn]);

  useEffect(() => {
    fetchCollaborators();
  }, [fetchCollaborators]);

  useEffect(() => {
    if (loadedCollaborators) {
      setCollaborators(loadedCollaborators);
    }
  }, [loadedCollaborators, setCollaborators]);

  const refetchFiles = useCallback(async () => {
    try {
      setIsUpdatingDocumentRequest(true);
      const [userResponse, filesResponse] = await Promise.all([
        getDocumentRequestUserFn({ id: documentRequestUserId }),
        getDocumentRequestUserFilesFn({
          accessToken: accessToken || undefined,
          id: documentRequestUserId,
          perPage: 1000,
        }),
      ]);

      if (filesResponse.data) {
        const updatedFiles = filesResponse.data.items;
        setDocumentRequestUserFiles(current => [
          ...current.filter(file => !updatedFiles.some(updatedFile => updatedFile.id === file.id)),
          ...updatedFiles,
        ]);
      }

      if (userResponse.data) {
        setDocumentRequest(userResponse.data);
      }
    } finally {
      setIsUpdatingDocumentRequest(false);
    }
  }, [accessToken, getDocumentRequestUserFilesFn, documentRequestUserId, getDocumentRequestUserFn]);

  useEffect(() => {
    getDocumentRequestUserFn({ id: documentRequestUserId });
  }, [getDocumentRequestUserFn, documentRequestUserId]);

  useEffect(() => {
    if (creatorSlug) {
      getCreatorFn({ creatorSlug });
    }
  }, [getCreatorFn, creatorSlug]);

  useEffect(() => {
    if (documentRequest?.userWorkflowId) {
      getWorkflow({ userWorkflowId: documentRequest.userWorkflowId });
    }
  }, [documentRequest?.userWorkflowId, getWorkflow]);

  useEffect(() => {
    if ((accessToken || userId) && documentRequestUserId) {
      getDocumentRequestUserFilesFn({
        accessToken: accessToken || undefined,
        id: documentRequestUserId,
        perPage: 1000,
      });
    }
  }, [getDocumentRequestUserFilesFn, accessToken, userId, documentRequestUserId]);

  useEffect(() => {
    if (containerRef.current && !isTabsLayout) {
      setContainerOffset(containerRef.current.getBoundingClientRect().top);
    }
    if (filePreview && isTabsLayout) {
      setFilePreview(null);
    }
  }, [isTabsLayout, filePreview]);

  useEffect(() => {
    if (documentRequestUser?.compiledDocument?.id) {
      preloadDocument({
        accessToken,
        id: documentRequestUser.compiledDocument.id,
      })
        .then(blob => {
          const typeBlob = new Blob([blob], { type: documentRequestUser.compiledDocument?.mimeType });
          const blobPreviewUrl = window.URL.createObjectURL(typeBlob);
          setCompiledBlobUrl(blobPreviewUrl);
        })
        .catch(error => {
          logError(error);
        });
    }
  }, [documentRequestUser, accessToken]);

  useEffect(() => {
    if (documentRequestUserFilesLoaded) {
      setDocumentRequestUserFiles(documentRequestUserFilesLoaded.items);
      if (documentRequestUserFilesLoaded.items.filter(f => f.status !== DocumentRequestFileStatus.COMPLETE).length === 0) {
        setCurrentTab('uploaded');
      } else {
        setCurrentTab('pending');
      }
    }
  }, [documentRequestUserFilesLoaded]);

  useEffect(() => {
    if (documentRequestUser) {
      setDocumentRequest(documentRequestUser);
    }
  }, [documentRequestUser]);

  useEffect(() => {
    if (!isTableViewOpen || filePreview) {
      setIsCalculatorsViewOpen(false);
    }
  }, [isTableViewOpen, filePreview]);

  useEffect(() => {
    if (isCalculatorsViewOpen) {
      setFilePreview(null);
    }
  }, [isCalculatorsViewOpen]);

  const { addListener, removeListener } = useEventsContext();

  useEffect(() => {
    const listener = addListener('docRequestUpdate', ({ id: emittedId }) => {
      if (emittedId === documentRequestUserId) {
        refetchFiles();
      }
    });

    return () => removeListener('docRequestUpdate', listener);
  }, [documentRequestUserId, addListener, removeListener, refetchFiles]);

  const reOpenDocumentRequest = async (): Promise<void> => {
    setIsUpdatingDocumentRequest(true);
    await reOpenDocumentRequestFn({ accessToken: accessToken || undefined, documentRequestUserId });
    await refetchFiles();
  };

  const handleDocumentUploaded: NonNullable<Parameters<typeof DocumentFileCard>[0]['onFinishUpload']> = async ({ documentRequestUserFileId }) => {
    if(!creatorId && (documentRequest?.status === DocumentRequestStatus.IN_REVIEW || documentRequest?.status === DocumentRequestStatus.SUCCEEDED)) {
      await reOpenDocumentRequest();
    }
    const response = await getDocumentRequestUserFile({
      fileId: documentRequestUserFileId,
    });
    if (response.data) {
      const newDocumentRequestUserFile: ThisDocumentRequestUserFile = response.data;
      setDocumentRequestUserFiles(current => [...current.filter(file => file.id !== newDocumentRequestUserFile.id), newDocumentRequestUserFile]);
    }
  };

  const handleDocumentsChanged: Parameters<typeof DocumentFileCard>[0]['onChangeDocuments'] = (documentRequestUserFileId, newDocuments) => {
    setDocumentRequestUserFiles(current => current.map(file => {
      if (file.id === documentRequestUserFileId) {
        return {
          ...file,
          documents: newDocuments,
        };
      }
      return file;
    }));
  };

  const completeDocumentRequest = async (): Promise<void> => {
    setIsUpdatingDocumentRequest(true);
    await completeDocumentRequestFn({ accessToken: accessToken || undefined, documentRequestUserId });
    await refetchFiles();
  };

  const toggleSubmitForReviewModal = async (skipRefetch : boolean):Promise<void> => {
    await updateDocumentRequestUserModal({
      id: documentRequestUserId,
      isCreator: !!creatorId,
      modalClose: true,
    });
    if(!skipRefetch) {
      refetchFiles();
    }
  };

  const submitDocumentRequest = async (): Promise<void> => {
    setIsUpdatingDocumentRequest(true);
    await updateDocumentRequestUser({
      id: documentRequestUserId,
      status: DocumentRequestStatus.IN_REVIEW,
      userWorkflowId: documentRequestUser?.userWorkflowId || null,
    });
    await toggleSubmitForReviewModal(true);
    await refetchFiles();
    setIsConfirmSubmitModalOpen(false);
  };

  useEffect(() => {
    if (documentRequest?.type === 'income-data' && documentRequest?.totalFiles === 0) {
      setAddDocumentModalOpen(true);
    }
  }, [documentRequest?.type, documentRequest?.totalFiles]);

  if (!documentRequest) {
    return (
      <ProgressBarLoader chunks={[
        {
          label: "Fetching document request...",
          loaded: false,
        },
      ]} />
    );
  }

  const requiredFiles = documentRequestUserFiles.filter(f => f.isRequired);
  const uploadedRequiredFiles = requiredFiles.filter(f => f.status === DocumentRequestFileStatus.COMPLETE);
  const isComplete = documentRequest.status === DocumentRequestStatus.SUCCEEDED;
  const isInReview = documentRequest.status === DocumentRequestStatus.IN_REVIEW;
  const isCreator = creatorId === documentRequest?.creatorId;
  const modalStateFromDbIsClosed = isCreator
    ? documentRequest?.submitForReviewModalCloseForAttorney
    : documentRequest?.submitForReviewModalCloseForClient;
  const noTabs = documentRequestUserFiles.filter(f => f.status !== DocumentRequestFileStatus.COMPLETE).length === 0
    || documentRequestUserFiles.filter(f => f.status === DocumentRequestFileStatus.COMPLETE).length === 0;

  const outstandingFiles = documentRequestUserFiles
    .filter(f => f.status !== DocumentRequestFileStatus.COMPLETE)
    .sort((a, b) => (a.order || a.documentRequestFile?.order || 0) - (b.order || b.documentRequestFile?.order || 0))
    .sort((a, b) => (a.isRequired ? -1 : 1) - (b.isRequired ? -1 : 1));

  const uploadedFiles = documentRequestUserFiles
    .filter(f => f.status === DocumentRequestFileStatus.COMPLETE)
    .sort((a, b) => (a.order || a.documentRequestFile?.order || 0) - (b.order || b.documentRequestFile?.order || 0))
    .sort((a, b) => (a.isRequired ? -1 : 1) - (b.isRequired ? -1 : 1))
    .sort((a, b) => {
      const getMostRecentCreationDate = (documents: Array<{ document: { createdAt: string } | null }>): number => {
        const timestamps = documents
          .map(doc => doc.document?.createdAt)
          .filter(removeNullish)
          .map(date => new Date(date).getTime());

        return Math.max(...timestamps);
      };

      const aMostRecentDate = getMostRecentCreationDate(a.documents);
      const bMostRecentDate = getMostRecentCreationDate(b.documents);

      return bMostRecentDate - aMostRecentDate;
    });

  const downloadFiles = (overrideToken?: string ): void => {
    if (!accessToken && !overrideToken) {
      return;
    }
    if (uploadedFiles && documentRequest) {
      const allDocumentIds: string[] = [];
      uploadedFiles.forEach((file) => file.documents.forEach((document) => {
        if (document.document) {
          allDocumentIds.push(document.document.id);
        }
      }));
      const pdfDownloadLink = getUrlBase('/documents-proxy/documents/archive.pdf', {
        accessToken: accessToken || overrideToken,
        archiveName: documentRequest.documentRequest.title,
        documentId: allDocumentIds,
      });
      const a = window.document.createElement('a');
      a.href = pdfDownloadLink;
      a.style.display = 'none';
      a.download = `${documentRequest.documentRequest.title}.pdf`;
      window.document.body.appendChild(a);
      a.click();
      a.remove();
    }
  };

  const areAllRequiredUploaded = uploadedFiles.length !== 0
    && requiredFiles.length === uploadedRequiredFiles.length
    && !isFetchingDocumentRequestUserFiles;

  const lastUpdated = documentRequestUserFiles
    .flatMap(file => file.documents)
    .map(doc => doc.document?.createdAt)
    .filter(removeNullish)
    .sort((a, b) => new Date(b).getTime() - new Date(a).getTime())[0];

  return (
    <>
      {isAddDocumentModalOpen && (
        <>
          {documentRequest.type === 'income-data'
            ? <AddIncomeSourceModal
              onClose={() => setAddDocumentModalOpen(false)}
              documentRequestUserId={documentRequestUserId}
              refetchFiles={refetchFiles}
              refetchConsolidatedData={fetchConsolidatedData}
              isTableViewOpen={isTableViewOpen}
            />
            : <AddDocumentModal
              onClose={() => setAddDocumentModalOpen(false)}
              documentRequestUserId={documentRequestUserId}
              refetchFiles={refetchFiles} />
          }
        </>
      )}
      <Head>
        <title>{isInDashboard ? `${documentRequest.documentRequest.title} · Glade Dashboard` : documentRequest.documentRequest.title}</title>
      </Head>
      <DocumentAccessGate
        documentCreatorId={documentRequest.creatorId}
        documentPersonId={documentRequest.personId}
        isCollaborator={documentRequest.isCollaborator}
        isCenteredVertically
      >
        <MainPanelSubLayout
          homeTabTitle="Documents"
          mainPanelClassName={isTableViewOpen ? s.unsetOverflowHidden : undefined}
          header={{
            actions: [
              ...(isCreator && !isInDashboard && documentRequest.userWorkflowId
                ? [
                  {
                    icon: Workflow,
                    isSecondary: true,
                    label: 'View workflow',
                    onClick: () =>
                      router.push(getUrl(IDENTIFIERS.DASHBOARD_USER_WORKFLOW_DETAIL, { userWorkflowId: documentRequest.userWorkflowId as string })),
                  },
                ]
                : []),
              ...((documentRequest.documentRequest.isForCreator ? !isCreator || isInReview : isCreator)
              && !isComplete
              && areAllRequiredUploaded
              && isInReview
                ? [
                  {
                    icon: Check,
                    label: 'Mark document request as completed',
                    onClick: completeDocumentRequest,
                  },
                ]
                : []),
              ...((documentRequest.documentRequest.isForCreator ? !isCreator || isInReview : isCreator) && isComplete
                ? [
                  {
                    icon: Reopen,
                    label: 'Re-open document request',
                    onClick: reOpenDocumentRequest,
                  },
                ]
                : []),
              ...(!isComplete && (isCreator || documentRequest.type === 'income-data')
                ? [
                  {
                    icon: Plus,
                    isSecondary: true,
                    label: `Add ${documentRequest.type === 'income-data' ? 'Income Source' : 'an item'}`,
                    onClick: () => setAddDocumentModalOpen(true),
                  },
                ]
                : []),
              ...(uploadedFiles.length !== 0 && isCreator
                ? [
                  {
                    icon: FilePdf,
                    isSecondary: true,
                    label: 'Download PDF',
                    onClick: () => downloadFiles(),
                  },
                ]
                : []),
              ...(canShowTable && documentRequest.type === 'income-data'
                ? [{
                  icon: isTableViewOpen ? ToggleRight : ToggleLeft,
                  iconWeight: isTableViewOpen ? 'fill' as IconWeight : undefined,
                  isSecondary: true,
                  label: 'Table view',
                  onClick: () => setIsTableViewOpen(!isTableViewOpen),
                }]
                : []),
              ...(canShowTable && isTableViewOpen && documentRequest.type === 'income-data' && isCreator
                ? [{
                  icon: isCalculatorsViewOpen ? ToggleRight : ToggleLeft,
                  iconWeight: isCalculatorsViewOpen ? 'fill' as IconWeight : undefined,
                  isSecondary: true,
                  label: 'Calculators',
                  onClick: () => setIsCalculatorsViewOpen(!isCalculatorsViewOpen),
                }]
                : []),
              ...(!isComplete && !isInReview && areAllRequiredUploaded
                ? [
                  {
                    icon: PaperPlaneRight,
                    label: 'Submit for review',
                    onClick: () => setIsConfirmSubmitModalOpen(true),
                  },
                ]
                : []),
            ],
            description: (
              <>
                {!areAllRequiredUploaded && !isCreator && !documentRequest.documentRequest.isForCreator && (
                  <p className="body-sm">You can only submit this document upload request for review after all required documents are uploaded.</p>
                )}
                {lastUpdated && (
                  <p className="body-sm" style={{ color: 'var(--color-gray-75)' }}>
                    Last activity: {format.datetimeLocalTZ.withoutTimezone({ datetime: lastUpdated })}
                  </p>
                )}
              </>
            ),
            title: documentRequest.documentRequest.title,
          }}
          pageContext={{
            isFetching: !workflowData,
            items: workflowData ? userWorkflowToContext(workflowData) : [],
          }}
          rightPanel={isTableViewOpen
            ? undefined
            :{
              component: (
                <>
                  {
                    <div className={s.rightPanel}>
                      <Header hierarchy="h2" title="Discussion" isSticky />
                      {workflowData && (
                        <UserWorkflowDiscussion
                          loadedCollaborators = {collaborators}
                          isLoaded={fetchingWorkflow?.isFetching === false}
                          userWorkflow={workflowData}
                          contextId={documentRequestUserId}
                          contextType="document-request"
                          isAsync
                        />
                      )}
                    </div>
                  }
                </>
              ),
              title: 'Discussion',
            }}
        >
          {isTableViewOpen
            ? <>
              <div style={{ alignItems: 'flex-start', display: 'flex', flex: 1, flexDirection: 'row' }}>
                <div className={s.table} style={filePreview && { maxWidth: '50vw' } || isCalculatorsViewOpen && { maxWidth: '70vw' } || {}}>
                  <Table className={s.tableBottomSpacer}>
                    <THead>
                      <TableRow>
                        <TH colSpan={2} label="Calculators" className={s.thCalculators} />
                        <TH colSpan={4} label="Income" className={s.thIncome} />
                        <TH colSpan={9} label="Deductions" className={s.thDeductions} />
                        <TH colSpan={1} label="Net Income" />
                      </TableRow>
                      <TableRow>
                        {TABLE_COLUMNS.map((column, idx) => (
                          <TH
                            key={column}
                            columnId={column}
                            className={idx === 0 ? s.firstColumn : undefined}
                            sort={
                              column === 'Pay Date'
                                ? {
                                  currentSortByField: 'Pay Date',
                                  currentSortDirection: payDateSortDirection,
                                  onSort: (_field, direction) => {
                                    setPayDateSortDirection(direction);
                                  },
                                }
                                : undefined
                            }
                          >
                            <p className={s.columnHeadCell}>
                              {(column === 'Include in Monthly Totals' && 'Include in Monthly Totals in Schedule I') || (column === 'Employer' && 'Income Source') || (column === "Amount per Pay Period" && "Amount per Month") || column}
                            </p>
                            {idx === 0 && <Button
                              variant='secondary'
                              size='xs'
                              onClick={() => {
                                if (collapsedRows.length === 0) {
                                  const allRowIds = consolidatedData?.rows?.flatMap(row => [
                                    row.id,
                                    ...row.months.flatMap(month => [
                                      month.id,
                                      ...month.rows.map(subRow => subRow.id),
                                    ]),
                                  ]) ?? [];
                                  setCollapsedRows(allRowIds);
                                } else {
                                  setCollapsedRows([]);
                                }
                              }}
                              className={s.collapseButton}
                            >
                              {collapsedRows.length === 0
                                ? <><ArrowsInLineVertical style={{ marginRight: 4 }} /> Collapse all rows</>
                                : <><ArrowsOutLineVertical style={{ marginRight: 4 }} /> Expand all rows</>}
                            </Button>}
                          </TH>
                        ))}
                      </TableRow>
                    </THead>
                    {consolidatedData?.rows
                      .map((row, rowIndex) => (
                        <TBody key={rowIndex} className={s.incomeSourceRow}>
                          <TableRow key={rowIndex}>
                            {TABLE_COLUMNS.map((colName, columnIndex) => (
                              <TD
                                key={`${rowIndex}-${columnIndex}`}
                                addButton={colName === 'Employer'
                                  ? {
                                    label: "Add item",
                                    onClick: () => {
                                      setUserFileIdForAddLine(row.id);
                                    },
                                  }
                                  : undefined}>
                                <Buttons
                                  isWrapper
                                  onClick={() => colName === 'Employer'
                                    ? setCollapsedRows(prev => prev.includes(row.id) ? prev.filter(r => r !== row.id) : [...prev, row.id])
                                    : null
                                  }
                                >
                                  <div className={s.cell}>
                                    {colName === 'Include in Monthly Totals' && (
                                      <>
                                        <Checkbox
                                          disabled={(!row.incomeSource || row.incomeSource==='Employment') && row.months.length === 0}
                                          isChecked={selectedDocuments.some(d => row.months.find((m) => m.rows.find((r) => r.id === d)))}
                                          label=''
                                          size='sm'
                                          onChange={newValue => {
                                            const rowIds = row.months
                                              .map(r => r.rows.map((rr) => rr.id)).reduce((acc, val) => [...acc, ...val], []);
                                            if (newValue) {
                                              setSelectedDocuments(prev => [
                                                ...prev.filter(d => !rowIds.includes(d)),
                                                ...rowIds,
                                              ]);
                                            }
                                            else {
                                              setSelectedDocuments(prev => prev.filter(d => !rowIds.includes(d)));
                                            }
                                            Bluebird.mapSeries(row.months, async (month) => {
                                              await Bluebird.mapSeries(month.rows, async (document) => {
                                                const data = fetchedData?.files
                                                  .find((f) => f.id === row.id)?.documents.find((d) => d.fileDocumentId === document.id)?.data;
                                                if (data?.tableData) {
                                                  await updateInternalDataFn({
                                                    data: {
                                                      ...data,
                                                      useInCalculation: newValue,
                                                    },
                                                    documentId: document.id,
                                                  });
                                                }
                                              });
                                            })
                                              .then(() => fetchConsolidatedData());
                                          }}
                                        />
                                      </>
                                    )}

                                    {colName === 'Include in Means Test' && (
                                      <>
                                        <Checkbox
                                          disabled={(!row.incomeSource || row.incomeSource==='Employment') && row.months.length === 0}
                                          isChecked={selectedForMeanTest.some(d => row.months.find((m) => m.rows.find((r) => r.id === d)))}
                                          label=''
                                          size='sm'
                                          onChange={newValue => {
                                            const rowIds = row.months
                                              .map(r => r.rows.map((rr) => rr.id)).reduce((acc, val) => [...acc, ...val], []);
                                            if (newValue) {
                                              setSelectedForMeanTest(prev => [
                                                ...prev.filter(d => !rowIds.includes(d)),
                                                ...rowIds,
                                              ]);
                                            }
                                            else{
                                              setSelectedForMeanTest(prev => prev.filter(d => !rowIds.includes(d)));
                                            }
                                            Bluebird.mapSeries(row.months, async (month) => {
                                              await Bluebird.mapSeries(month.rows, async (document) => {
                                                const data = fetchedData?.files
                                                  .find((f) => f.id === row.id)?.documents.find((d) => d.fileDocumentId === document.id)?.data;
                                                if (data?.tableData) {
                                                  await updateInternalDataFn({
                                                    data: {
                                                      ...data,
                                                      useInMeansTest: newValue,
                                                    },
                                                    documentId: document.id,
                                                  });
                                                }
                                              });
                                            })
                                              .then(() => fetchConsolidatedData());
                                          }}
                                        />
                                      </>
                                    )}
                                    {colName === 'Employer'
                                      && <Buttons
                                        isWrapper
                                        onClick={(e) => {
                                          e.stopPropagation();
                                          setModalOpen(row.id);
                                        }}
                                      >
                                        {typeof row[colName as keyof FlatConsolidatedData] === 'number'
                                          ? formatAmountWithCurrency(row[colName as keyof FlatConsolidatedData] as number, Currency.Usd)
                                          : (row[colName as keyof FlatConsolidatedData] || '—')}
                                      </Buttons>
                                    }
                                    { colName !== 'Include in Monthly Totals' && colName !== 'Include in Means Test' && colName !== 'Employer' && <span className='caption'>
                                      {typeof row[colName as keyof FlatConsolidatedData] === 'number'
                                        ? formatAmountWithCurrency(row[colName as keyof FlatConsolidatedData] as number, Currency.Usd)
                                        : (row[colName as keyof FlatConsolidatedData] || '—')}
                                    </span>

                                    }

                                    {colName === 'Employer' && row.months.length > 0 && (
                                      <div className={s.caretDecorator}>
                                        {collapsedRows.includes(row.id) ? <CaretDown size={10} /> : <CaretUp size={10} />}
                                      </div>
                                    )}
                                  </div>
                                </Buttons>
                              </TD>
                            ))}
                          </TableRow>
                          {!collapsedRows.includes(row.id) && row.months
                            .sort((rowA, rowB) => {
                              const dateA = rowA?.['Pay Date'] ? new Date(rowA['Pay Date']).getTime() : 0;
                              const dateB = rowB?.['Pay Date'] ? new Date(rowB['Pay Date']).getTime() : 0;
                              return payDateSortDirection === 'asc' ? dateA - dateB : dateB - dateA;
                            })
                            .map((month, monthIndex) => (
                              <>
                                <TableRow key={`${rowIndex}-${monthIndex}`} className={s.subRow}>
                                  {TABLE_COLUMNS.map((colName, columnIndex) => (
                                    <TD key={`${rowIndex}-${columnIndex}`}>
                                      <Buttons
                                        isWrapper
                                        onClick={() => {
                                          if (colName === 'Employer') {
                                            setCollapsedRows(prev => prev.includes(month.id)
                                              ? prev.filter(r => r !== month.id)
                                              : [...prev, month.id]);
                                          }
                                        }}
                                      >
                                        <div className={s.cell}>
                                          {
                                            colName === 'Include in Monthly Totals' && <>
                                              <Checkbox
                                                isChecked={selectedDocuments.some(d => month.rows.find((r) => r.id === d))}
                                                label=""
                                                size="sm"
                                                onChange={newValue => {
                                                  setSelectedDocuments(prev => prev.includes(month.id)
                                                    ? prev.filter(r => r !== month.id)
                                                    : [...prev, month.id],
                                                  );
                                                  Bluebird.mapSeries(month.rows, async (subRow) => {
                                                    const rowData = fetchedData && fetchedData
                                                      .files.find((f) => f.id === row.id)?.documents
                                                      .find((d) => d.fileDocumentId === subRow.id)?.data;
                                                    if (rowData?.tableData) {
                                                      await updateInternalDataFn({
                                                        data: {
                                                          ...rowData,
                                                          useInCalculation: newValue,
                                                        },
                                                        documentId: subRow.id,
                                                      });
                                                    }
                                                  })
                                                    .then(() => fetchConsolidatedData());
                                                }}
                                              />
                                            </>
                                          }

                                          {
                                            colName === 'Include in Means Test' && <>
                                              <Checkbox
                                                isChecked={selectedForMeanTest.some(d => month.rows.find((r) => r.id === d))}
                                                label=""
                                                size="sm"
                                                onChange={newValue => {
                                                  setSelectedForMeanTest(prev => prev.includes(month.id)
                                                    ? prev.filter(r => r !== month.id)
                                                    : [...prev, month.id],
                                                  );
                                                  Bluebird.mapSeries(month.rows, async (subRow) => {
                                                    const rowData = fetchedData && fetchedData
                                                      .files.find((f) => f.id === row.id)?.documents
                                                      .find((d) => d.fileDocumentId === subRow.id)?.data;
                                                    if (rowData?.tableData) {
                                                      await updateInternalDataFn({
                                                        data: {
                                                          ...rowData,
                                                          useInMeansTest: newValue,
                                                        },
                                                        documentId: subRow.id,
                                                      });
                                                    }
                                                  })
                                                    .then(() => fetchConsolidatedData());
                                                }}
                                              />
                                            </>
                                          }

                                          {colName === 'Employer'
                                            && (
                                              <>
                                                <div style={{ width: 16 }} />
                                                <Buttons
                                                  isWrapper
                                                  onClick={(e) => {
                                                    e.stopPropagation();
                                                    setModalOpen(row.id);
                                                  }}
                                                >
                                                  {/* eslint-disable-next-line no-nested-ternary */}
                                                  {month.Employer
                                                    ? row.incomeSource === 'Employment'
                                                      ? month.Employer
                                                      : new Date(month.Employer).toLocaleString('default', { month: 'long' })
                                                    : '—'}
                                                </Buttons>
                                              </>
                                            )}

                                          {colName === 'Net Pay' && (
                                            <span className="caption">
                                              {format.price.withCurrency(month['Net Pay'] as number ?? calculateNetPay(month), 'USD')}
                                            </span>
                                          )}

                                          {colName !== 'Include in Monthly Totals' && colName !== 'Include in Means Test' && colName !== 'Employer' && colName !== 'Net Pay'
                                            && <span
                                              className="caption">{typeof month[colName as keyof FlatConsolidatedData] === 'number'
                                                ? formatAmountWithCurrency(month[colName as keyof FlatConsolidatedData] as number, Currency.Usd)
                                                : (month[colName as keyof FlatConsolidatedData] || '—')}
                                            </span>}
                                          {colName === 'Employer' && month.rows.length > 0 && (
                                            <>
                                              {` (${month.rows.length})`}
                                              <div className={s.caretDecorator}>
                                                {collapsedRows.includes(month.id) ? <CaretDown size={10} /> : <CaretUp size={10} />}
                                              </div>
                                            </>
                                          )}
                                        </div>
                                      </Buttons>
                                    </TD>
                                  ))}
                                </TableRow>
                                {!collapsedRows.includes(month.id) && month.rows
                                  .sort((rowA, rowB) => {
                                    const dateA = rowA?.['Pay Date'] ? new Date(rowA['Pay Date']).getTime() : 0;
                                    const dateB = rowB?.['Pay Date'] ? new Date(rowB['Pay Date']).getTime() : 0;
                                    return payDateSortDirection === 'asc' ? dateA - dateB : dateB - dateA;
                                  })
                                  .map((subRow, subIndex) => {
                                    const subRowDocument = fetchedData
                                      && fetchedData.files.find((f) => f.id === row.id)?.documents.find((d) => d.fileDocumentId === subRow.id);
                                    const rowData = subRowDocument?.data;
                                    const rowDocumentId = subRowDocument?.document?.id;
                                    return (
                                      <>
                                        <TableRow
                                          key={`${monthIndex}-${subIndex}`}
                                          className={
                                            (rowDocumentId !== undefined && filePreview?.documentId !== undefined
                                            && rowDocumentId === filePreview.documentId)
                                              ? s.subRowSelected
                                              : s.subRow}>
                                          {TABLE_COLUMNS.map((colName, columnIndex) => (
                                            <TD key={`${rowIndex}-${columnIndex}`}>
                                              <Buttons
                                                isWrapper
                                                onClick={() => {
                                                  if (colName === 'Employer') {
                                                    setCollapsedRows(prev => prev.includes(subRow.id)
                                                      ? prev.filter(r => r !== subRow.id)
                                                      : [...prev, subRow.id]);
                                                  }
                                                }}
                                              >
                                                <div className={s.cell}>
                                                  {
                                                    colName === 'Include in Monthly Totals' && <>
                                                      <Checkbox
                                                        isChecked={selectedDocuments.includes(subRow.id)}
                                                        label=""
                                                        size="sm"
                                                        onChange={newValue => {
                                                          setSelectedDocuments(prev => prev.includes(subRow.id)
                                                            ? prev.filter(r => r !== subRow.id)
                                                            : [...prev, subRow.id],
                                                          );
                                                          if (rowData?.tableData) {
                                                            updateInternalDataFn({
                                                              data: {
                                                                ...rowData,
                                                                useInCalculation: newValue,
                                                              },
                                                              documentId: subRow.id,
                                                            })
                                                              .then(() => fetchConsolidatedData());
                                                          }
                                                        }}
                                                      />
                                                    </>
                                                  }

                                                  {
                                                    colName === 'Include in Means Test' && <>
                                                      <Checkbox
                                                        isChecked={selectedForMeanTest.includes(subRow.id)}
                                                        label=""
                                                        size="sm"
                                                        onChange={newValue => {
                                                          setSelectedForMeanTest(prev => prev.includes(subRow.id)
                                                            ? prev.filter(r => r !== subRow.id)
                                                            : [...prev, subRow.id],
                                                          );
                                                          if (rowData?.tableData) {
                                                            updateInternalDataFn({
                                                              data: {
                                                                ...rowData,
                                                                useInMeansTest: newValue,
                                                              },
                                                              documentId: subRow.id,
                                                            })
                                                              .then(() => fetchConsolidatedData());
                                                          }
                                                        }}
                                                      />
                                                    </>
                                                  }

                                                  {colName === 'Employer'
                                                    && <>
                                                      <div style={{ width: 32 }} />
                                                      <Tooltip text="Generated by Glade AI">
                                                        <NoodleAI size={16} />
                                                      </Tooltip>
                                                      <Buttons
                                                        className={s.docButton}
                                                        isWrapper
                                                        onClick={() => {
                                                          let documentId;
                                                          for (let i = 0; i < documentRequestUserFiles.length; i += 1) {
                                                            const matchingDocument = documentRequestUserFiles[i].documents
                                                              .find((d) => d.fileDocumentId === subRow.id);
                                                            if (matchingDocument) {
                                                              documentId = matchingDocument.document?.id;
                                                              break;
                                                            }
                                                          }
                                                          if (isTabsLayout && documentId) {
                                                            openDocumentRequestUserFilePane({ documentRequestUserFileId: row.id });
                                                            setDocumentId(documentId);
                                                          } else if (documentId) {
                                                            setFilePreview(filePreview && filePreview.documentId === documentId
                                                              ? null
                                                              : {
                                                                documentId,
                                                                documentRequestUserFileId: row.id,
                                                              });
                                                          }
                                                        }}
                                                      >
                                                        {/* eslint-disable-next-line no-nested-ternary */}
                                                        {subRow.Employer
                                                          ? (
                                                            new Date(`${subRow.Employer}T00:00:00`).toLocaleString('default', { month: 'long' }) === 'Invalid Date' ? subRow.Employer : new Date(`${subRow.Employer}T00:00:00`).toLocaleString('default', { month: 'long' })
                                                          )
                                                          : '—'}
                                                      </Buttons>
                                                      <Button variant="secondary" size="xs" className={s.editButton} onClick={() => setModalOpen(subRow.id)}>Edit</Button>
                                                    </>}

                                                  {colName === 'Net Pay' && (
                                                    <span className="caption">
                                                      {format.price.withCurrency(subRow['Net Pay'] as number ?? calculateNetPay(subRow), 'USD')}
                                                    </span>
                                                  )}

                                                  {colName !== 'Include in Monthly Totals' && colName !== 'Include in Means Test' && colName !== 'Employer' && colName !== 'Net Pay'
                                                    && <span
                                                      className="caption">{typeof subRow[colName as keyof FlatConsolidatedData] === 'number' || subRow[colName as keyof FlatConsolidatedData] === null
                                                        ? formatAmountWithCurrency(
                                                        subRow[colName as keyof FlatConsolidatedData] as number || 0,
                                                        Currency.Usd,
                                                        )
                                                        : (month[colName as keyof FlatConsolidatedData] || '—')}
                                                    </span>}
                                                </div>
                                              </Buttons>
                                            </TD>
                                          ))}
                                        </TableRow>
                                        {rowData?.tableData && modalOpen === subRow.id && <Modal drag hasForm title={`Edit income data`} align={filePreview ? 'left' : 'center'} onClose={() => setModalOpen(null)}>
                                          <form onSubmit={
                                            (e: React.FormEvent<HTMLFormElement>) => {
                                              e.preventDefault();
                                              const formData = new FormData(e.currentTarget as HTMLFormElement);
                                              updateInternalDataFn({
                                                data: {
                                                  ...rowData,
                                                  tableData: {
                                                    ...(rowData.tableData as Record<string, unknown>),
                                                    ...Object.fromEntries(Array.from(formData.entries()).map(([key, value]) => [key, key === 'payDate' ? value : Number(value)])),
                                                  },
                                                },
                                                documentId: subRow.id,
                                              })
                                                .then(async () => {
                                                  await fetchConsolidatedData();
                                                  setModalOpen(null);
                                                });
                                            }
                                          } className={s.paystubModal}>
                                            {Object.entries(PAY_ORGANIZER_KEY_TO_LABEL)
                                              .filter(([, label]) => label !== 'Net Pay')
                                              .sort(([, labelA], [, labelB]) => {
                                                const indexA = TABLE_COLUMNS.findIndex(col => col === labelA);
                                                const indexB = TABLE_COLUMNS.findIndex(col => col === labelB);
                                                return indexA - indexB;
                                              })
                                              .map(([key, label]) => (
                                                <Label key={key} label={key === 'payDate' ? label : `${label} (USD)`} compact>
                                                  <input
                                                    type={key === 'payDate' ? 'date' : 'string'}
                                                    name={key}
                                                    id={key}
                                                    onClick={(e) => e.currentTarget.select()}
                                                    defaultValue={(rowData?.tableData as Record<string, string | undefined>)[key] || ''}
                                                  />
                                                </Label>
                                              ))
                                            }
                                            <Button
                                              loading={isRemovingDocument}
                                              variant="destructive"
                                              size="md"
                                              onClick={() => removeFile({
                                                documentRequestUserFileId: row.id,
                                                fileDocumentId: subRow.id,
                                              })}
                                            >
                                              Delete
                                            </Button>
                                            <Button variant="primary" type="submit" size="md">
                                              Submit
                                            </Button>
                                          </form>
                                        </Modal>}
                                      </>
                                    );
                                  })}
                              </>
                            ))}
                        </TBody>
                      ))}
                  </Table>
                  {userFileIdForAddLine !== null && (
                    <>
                      <AddPayOrganizerItem
                        onSave={async () => {
                          await fetchConsolidatedData();
                        }}
                        userFileId={userFileIdForAddLine}
                        onClose={() => setUserFileIdForAddLine(null)}
                        documentRequestUserId={documentRequestUserId}
                      />
                    </>
                  )}
                </div>
                {isCalculatorsViewOpen && <div className={s.calculators}>
                  {canShowSchedulesICalculator && (
                    <SchedulesICalculator
                      fetchingData={fetchingConsolidatedDataState.isFetching}
                      data={fetchedData?.total as SchedulesIResponse}
                    />
                  )}
                  <MeansTestSection
                    meansTestData={meansTestData}
                    tableData={fetchedData}
                    selectedForMeanTest={selectedForMeanTest}
                    userWorkflowId={documentRequest?.userWorkflowId}
                    documentRequestUserId={documentRequestUserId}
                    fetchingMeansTestData={fetchingMeansTestData}
                  />
                </div>}
                {filePreview && <PayOrganizerFilePreview
                  documentRequestUserFileId={filePreview.documentRequestUserFileId}
                  documentId={filePreview.documentId}
                  onClose={() => setFilePreview(null)}
                />}
              </div>
              {valueModalOpen && <Modal onClose={() => setValueModalOpen(false)} hasPadding title="Update value">
                <Input
                  placeholder="Enter value"
                  compact
                  label="Value"
                />
                <Spacer />
                <Button fullWidth size="md" variant="primary" onClick={() => setValueModalOpen(false)}>
                  Save
                </Button>
              </Modal>}
            </>
            : <div className={s.wrapper} ref={containerRef}>
              {isCreator
                ? (
                  <>
                    {(isComplete || isInReview || (areAllRequiredUploaded && requiredFiles.length > 0)) && (
                      <div className={s.banner}>
                        <BannerStatus
                          status={isComplete ? 'success' : 'warning'}
                          label={
                            (isComplete && 'Document request complete.')
                            || (isInReview
                              && !documentRequest.documentRequest.isForCreator
                              && 'Contact has uploaded all required documents and has submitted this request for your review.')
                            || (isInReview
                              && documentRequest.documentRequest.isForCreator
                              && `You have uploaded all required documents and this request has been submitted.`)
                            || (documentRequest.documentRequest.isForCreator
                              && `All required documents have been uploaded, but you have not yet submitted this request.`)
                            || 'Contact has uploaded all required documents, but has not yet submitted this request for review. They may still be uploading optional documents.'
                          }
                          cta={!isComplete && !isInReview && !documentRequest.documentRequest.isForCreator
                            ? { label: 'Mark as completed anyway', onClick: completeDocumentRequest }
                            : undefined}
                        />
                      </div>
                    )}
                  </>
                )
                : (
                  <>
                    {(isComplete || isInReview || areAllRequiredUploaded) && !isFetchingDocumentRequestUserFiles && (
                      <div className={s.banner}>
                        <BannerStatus
                          status={isComplete ? 'success' : 'warning'}
                          label={
                            (isComplete && 'Document request complete.')
                            || (isInReview
                              && documentRequest.documentRequest.isForCreator
                              && `${creator?.name || 'The creator'} has uploaded all required documents and has submitted this request for your review.`)
                            || (isInReview && 'You have submitted all the required documents. Your documents are now under review by the provider.')
                            || (documentRequest.documentRequest.isForCreator
                              && `${
                                creator?.name || 'The creator'
                              } has uploaded all required documents but has not yet submitted this request for review. They may still be uploading optional documents.`)
                            || 'You have uploaded all required documents. If you are finished uploading documents, please submit.'
                          }
                          cta={!isComplete && !isInReview && documentRequest.documentRequest.isForCreator
                            ?{label:"Mark as completed anyway", onClick:completeDocumentRequest}
                            : undefined}
                        />
                      </div>
                    )}
                  </>
                )}
              {isTabsLayout
                ? (
                  <div className={s.container}>
                    {documentRequest.compiledDocument?.id && compiledBlobUrl && (
                      <>
                        <a href={compiledBlobUrl} className={s.download} download={documentRequest.compiledDocument.fileName}>
                          <FileArrowDown weight="fill" size={16} />
                          Download compiled documents
                        </a>
                        <Spacer size="16px" />
                      </>
                    )}
                    {!isCreator && !isComplete && !isInReview && !documentRequest.documentRequest.isForCreator && (
                      <>
                        {!isMobile && <Spacer size="16px" />}
                        <Buttons
                          isSecondary
                          isFullWidth
                          onClick={() => setIsConfirmSubmitModalOpen(true)}
                          isFetching={isUpdatingDocumentRequest}
                          disabled={!areAllRequiredUploaded}
                        >
                          Submit for review
                        </Buttons>
                        {!isMobile && <Spacer size="16px" />}
                        {!areAllRequiredUploaded && (
                          <div className={s.reviewInfo}>
                            <p>You can only submit this document upload request for review after all required documents are uploaded.</p>
                          </div>
                        )}
                        {isMobile && noTabs && <Spacer size={16} />}
                      </>
                    )}
                    <div className={s.files}>
                      {!noTabs && (
                        <Tabs
                          inputName="status"
                          inputs={[
                            {
                              id: 'pending',
                              label: `Pending (${documentRequestUserFiles.filter(f => f.status !== DocumentRequestFileStatus.COMPLETE).length})`,
                            },
                            {
                              id: 'uploaded',
                              label: `Uploaded (${documentRequestUserFiles.filter(f => f.status === DocumentRequestFileStatus.COMPLETE).length})`,
                            },
                          ]}
                          checked={currentTab}
                          onChange={newTab => setCurrentTab(newTab)}
                        />
                      )}
                      {isFetchingDocumentRequestUserFiles && <ProgressBarLoader chunks={[
                        {
                          label: 'Fetching files...',
                          loaded: false,
                        },
                      ]} />}
                      {(currentTab === 'pending' ? outstandingFiles : uploadedFiles).map(file => (
                        <DocumentFileCard
                          canDelete={isCreator && !isComplete}
                          canUpdateIsRequired={isCreator}
                          key={file.id}
                          creatorSlug={creatorSlug}
                          creatorName={creator?.name}
                          documentRequestUserId={documentRequestUserId}
                          documentRequestUserFileId={file.id}
                          fileName={file.fileName || file.documentRequestFile?.fileName || ''}
                          fileDescription={file.fileDescription || file.documentRequestFile?.fileDescription || ''}
                          onFinishUpload={handleDocumentUploaded}
                          isRequired={file.isRequired}
                          hasPermission={file.hasPermission}
                          isForYourEyesOnly={
                            file.isForYourEyesOnly !== null ? file.isForYourEyesOnly : file.documentRequestFile?.isForYourEyesOnly || false
                          }
                          refetchFiles={refetchFiles}
                          documents={file.documents}
                          onChangeDocuments={handleDocumentsChanged}
                          actionRequired={file.rejectionMessage
                            ? {
                              rejectedAt: file.rejectedAt,
                              rejectionMessage: file.rejectionMessage,
                            }
                            : undefined}
                          showDocumentValidationStatus={currentTab !== 'pending'}
                        />
                      ))}
                      {isComplete && !isCreator && (
                        <CustomLink
                          to={getUrl(IDENTIFIERS.USER_PROFILE, {
                            creatorSlug,
                          })}
                          className={s.successCTA}
                        >
                          <small>This document request is complete</small>
                          <p>View your chat</p>
                        </CustomLink>
                      )}
                    </div>
                  </div>
                )
                : <div className={s.containerColumns}>
                  {outstandingFiles.length > 0 && (
                    <div style={{ height: `calc(min(100vh, 100dvh) - ${containerOffset}px)` }}>
                      <Header hierarchy="h2" title={`${documentRequest.type === 'income-data' ? 'Income Sources' : 'Outstanding'} (${outstandingFiles.length})`} isSticky />
                      <div className={s.containerColumnsList}>
                        {outstandingFiles.map(file => (
                          <DocumentFileCard
                            canDelete={isCreator && !isComplete}
                            canUpdateIsRequired={isCreator}
                            key={file.id}
                            creatorSlug={creatorSlug}
                            creatorName={creator?.name}
                            documentRequestUserId={documentRequestUserId}
                            documentRequestUserFileId={file.id}
                            fileName={file.fileName || file.documentRequestFile?.fileName || ''}
                            fileDescription={file.fileDescription || file.documentRequestFile?.fileDescription || ''}
                            onFinishUpload={handleDocumentUploaded}
                            isRequired={file.isRequired}
                            hasPermission={file.hasPermission}
                            isIncomeSource={documentRequest.type === 'income-data'}
                            isForYourEyesOnly={
                              file.isForYourEyesOnly !== null ? file.isForYourEyesOnly : file.documentRequestFile?.isForYourEyesOnly || false
                            }
                            refetchFiles={refetchFiles}
                            documents={file.documents}
                            onChangeDocuments={handleDocumentsChanged}
                            actionRequired={file.rejectionMessage
                              ? {
                                rejectedAt: file.rejectedAt,
                                rejectionMessage: file.rejectionMessage,
                              }
                              : undefined}
                          />
                        ))}
                      </div>
                    </div>
                  )}
                  <div style={{ height: `calc(min(100vh, 100dvh) - ${containerOffset}px)` }}>
                    <Header hierarchy="h2" title={isFetchingDocumentRequestUserFiles ? 'Loading...' : `Uploaded (${uploadedFiles.length})`} isSticky />
                    <div className={s.containerColumnsList}>
                      {uploadedFiles.map(file => (
                        <DocumentFileCard
                          canDelete={isCreator && !isComplete}
                          canUpdateIsRequired={isCreator}
                          key={file.id}
                          creatorSlug={creatorSlug}
                          creatorName={creator?.name}
                          documentRequestUserId={documentRequestUserId}
                          documentRequestUserFileId={file.id}
                          fileName={file.fileName || file.documentRequestFile?.fileName || ''}
                          fileDescription={file.fileDescription || file.documentRequestFile?.fileDescription || ''}
                          onFinishUpload={handleDocumentUploaded}
                          isRequired={file.isRequired}
                          isIncomeSource={documentRequest.type === 'income-data'}
                          documents={file.documents}
                          onChangeDocuments={handleDocumentsChanged}
                          hasPermission={file.hasPermission}
                          isForYourEyesOnly={
                            file.isForYourEyesOnly !== null ? file.isForYourEyesOnly : file.documentRequestFile?.isForYourEyesOnly || false
                          }
                          refetchFiles={refetchFiles}
                          actionRequired={file.rejectionMessage
                            ? {
                              rejectedAt: file.rejectedAt,
                              rejectionMessage: file.rejectionMessage,
                            }
                            : undefined}
                          showDocumentValidationStatus
                        />
                      ))}
                    </div>
                  </div>
                </div>}

              {isConfirmSubmitModalOpen && (
                <Modal title="Confirm submission" onClose={() => setIsConfirmSubmitModalOpen(false)}>
                  <div className={s.confirmSubmit}>
                    {creator
                      ? (
                        <>
                          <UserImage size={80}
                            url={creator?.logoCompact?.url || creator?.person?.image} color={creator.primaryColour?.hex} name={creator.name} />
                          <h2 className="body-md-bold">
                          Your documents will be submitted{documentRequest.documentRequest.isForCreator ? '' : ` to ${creator?.name} for review`}.
                          </h2>
                          {!documentRequest.documentRequest.isForCreator && (
                            <p className="body-sm">
                            You will not be able to change any documents while your documents are in review. {creator.name} will notify you if any
                            documents need to be re-uploaded.
                            </p>
                          )}
                          <Buttons onClick={submitDocumentRequest} isSecondary isFullWidth isFetching={isUpdatingDocumentRequest}>
                          Submit
                          </Buttons>
                        </>
                      )
                      : (
                        <ProgressIndicator isCentered />
                      )}
                  </div>
                </Modal>
              )}
              {!modalStateFromDbIsClosed && !isComplete && !isInReview && areAllRequiredUploaded && documentRequest.type !== 'income-data' && (
                <Modal
                  hasPadding
                  onClose={() => {
                    toggleSubmitForReviewModal(false);
                  }}
                  closeIcon={<XCircle weight="fill" size={24} color="var(--color-gray-100)" />}
                  title=""
                >
                  <div className={s.modalContent}>
                    <div className={s.modalHeader}>
                      <WarningCircle size={96} weight="fill" color="var(--color-warning)" />
                      <h2 className={s.modalHeaderTitle}>Do you want to proceed with submission?</h2>
                      <p className={[s.modalBodyText, s.modalTextWarning].join(' ')}>
                        {isCreator ? 'Contact has': 'You have'} uploaded all required documents, but have not yet submitted this request for review. If {isCreator ? 'they': 'you'} are finished uploading documents, please submit the request now. Otherwise, close this window to continue uploading documents.
                      </p>
                    </div>
                    <div className={s.modalBodyButtons}>
                      <Button size='lg' className={s.modalBodyButton} variant='primary' onClick={submitDocumentRequest}>Submit</Button>
                      <Button size='lg' className={s.modalBodyButton} variant='secondary' onClick={() => toggleSubmitForReviewModal(false)}>Continue Uploading</Button>
                    </div>
                  </div>
                </Modal>
              )}
            </div>}
        </MainPanelSubLayout>
      </DocumentAccessGate>
    </>
  );
};

export default DocumentRequestUserDetail;
